import React, { useId, useState } from 'react';

const FormPreviewRenderer = ({ template }) => {
  const [formValues, setFormValues] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const previewId = useId();

  if (!template?.formElements?.length) return null;

  const handleChange = (id, value) => {
    setFormValues(prev => ({ ...prev, [id]: value }));
  };

  const renderElement = (el, idx) => {
    const key = `${el?.id}-${idx}`;
    const controlId = `${previewId}-${key}`;
    const value = formValues?.[key] || '';

    // Logo/media header
    if (el?.type === 'media') {
      return (
        <div key={key} className="flex justify-center mb-2">
          <img
            src={el?.src}
            alt={el?.alt || 'Form header'}
            className="h-10 object-contain"
          />
        </div>
      );
    }

    // Section heading
    if (el?.type === 'content') {
      return (
        <div key={key} className="pt-3 pb-1">
          <h3 className="text-sm font-semibold text-gray-800 border-b border-gray-200 pb-1">
            {el?.content || el?.label}
          </h3>
        </div>
      );
    }

    // Dropdown / select
    if (el?.type === 'select') {
      return (
        <div key={key} className="flex flex-col gap-1">
          <label htmlFor={controlId} className="text-xs font-medium text-gray-700">
            {el?.label}{el?.required && <span className="text-red-500 ml-0.5">*</span>}
          </label>
          <select
            id={controlId}
            className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
            value={value}
            onChange={e => handleChange(key, e?.target?.value)}
          >
            <option value="">{el?.placeholder || 'Select an option'}</option>
            {el?.options?.map((opt, oi) => (
              <option key={oi} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      );
    }

    // Radio buttons
    if (el?.type === 'radio') {
      return (
        <fieldset key={key} className="flex flex-col gap-1">
          <legend className="text-xs font-medium text-gray-700">
            {el?.label}{el?.required && <span className="text-red-500 ml-0.5">*</span>}
          </legend>
          <div className="flex flex-col gap-1">
            {el?.options?.map((opt, oi) => (
              <label key={oi} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={key}
                  value={opt}
                  checked={value === opt}
                  onChange={() => handleChange(key, opt)}
                  className="accent-emerald-500 w-3 h-3"
                />
                <span className="text-xs text-gray-600">{opt}</span>
              </label>
            ))}
          </div>
        </fieldset>
      );
    }

    // Checkboxes
    if (el?.type === 'checkbox') {
      const checked = formValues?.[key] || [];
      return (
        <fieldset key={key} className="flex flex-col gap-1">
          <legend className="text-xs font-medium text-gray-700">
            {el?.label}{el?.required && <span className="text-red-500 ml-0.5">*</span>}
          </legend>
          <div className="flex flex-col gap-1">
            {el?.options?.map((opt, oi) => (
              <label key={oi} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={checked?.includes(opt)}
                  onChange={e => {
                    const next = e?.target?.checked
                      ? [...checked, opt]
                      : checked?.filter(v => v !== opt);
                    handleChange(key, next);
                  }}
                  className="accent-emerald-500 w-3 h-3"
                />
                <span className="text-xs text-gray-600">{opt}</span>
              </label>
            ))}
          </div>
        </fieldset>
      );
    }

    // File upload
    if (el?.type === 'file') {
      return (
        <div key={key} className="flex flex-col gap-1">
          <label htmlFor={controlId} className="text-xs font-medium text-gray-700">
            {el?.label}{el?.required && <span className="text-red-500 ml-0.5">*</span>}
          </label>
          <input
            id={controlId}
            type="file"
            required={Boolean(el?.required)}
            onChange={(event) => handleChange(key, event?.target?.files?.[0]?.name || '')}
            className="w-full border-2 border-dashed border-gray-300 rounded p-3 text-xs text-gray-600 bg-gray-50 file:mr-3 file:rounded file:border-0 file:bg-emerald-100 file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-emerald-800 hover:file:bg-emerald-200"
          />
          {value && <span className="text-xs text-gray-500">Selected: {value}</span>}
        </div>
      );
    }

    // Slider
    if (el?.type === 'slider') {
      return (
        <div key={key} className="flex flex-col gap-1">
          <label htmlFor={controlId} className="text-xs font-medium text-gray-700">
            {el?.label}{el?.required && <span className="text-red-500 ml-0.5">*</span>}
          </label>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">{el?.min ?? 0}</span>
            <input
              id={controlId}
              type="range"
              min={el?.min ?? 0}
              max={el?.max ?? 10}
              value={value || el?.min || 0}
              onChange={e => handleChange(key, e?.target?.value)}
              className="flex-1 accent-emerald-500"
            />
            <span className="text-xs text-gray-500">{el?.max ?? 10}</span>
            <span className="text-xs font-medium text-emerald-600 w-4">{value || el?.min || 0}</span>
          </div>
        </div>
      );
    }

    // Textarea (multiline text)
    if (el?.multiline) {
      return (
        <div key={key} className="flex flex-col gap-1">
          <label htmlFor={controlId} className="text-xs font-medium text-gray-700">
            {el?.label}{el?.required && <span className="text-red-500 ml-0.5">*</span>}
          </label>
          <textarea
            id={controlId}
            placeholder={el?.placeholder || ''}
            value={value}
            onChange={e => handleChange(key, e?.target?.value)}
            rows={3}
            className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500 resize-none"
          />
        </div>
      );
    }

    // Default: text/email/phone/number/date/time/url input
    const inputType = el?.id === 'email' ? 'email'
      : el?.id === 'phone' ? 'tel'
      : el?.id === 'number' ? 'number'
      : el?.id === 'date' ? 'date'
      : el?.id === 'time' ? 'time'
      : el?.id === 'url'? 'url' :'text';

    return (
      <div key={key} className="flex flex-col gap-1">
        <label htmlFor={controlId} className="text-xs font-medium text-gray-700">
          {el?.label}{el?.required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        <input
          id={controlId}
          type={inputType}
          placeholder={el?.placeholder || ''}
          value={value}
          onChange={e => handleChange(key, e?.target?.value)}
          className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
        />
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <div className="bg-gray-50 border-b border-gray-200 px-4 py-2 flex items-center gap-2">
        <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
        <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
        <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
        <span className="ml-2 text-xs text-gray-500 font-medium">{template?.title} — Live Preview</span>
      </div>
      <div className="p-5 max-h-[420px] overflow-y-auto space-y-3">
        {template?.formElements?.map((el, idx) => renderElement(el, idx))}
        <div className="pt-2">
          <button
            type="button"
            className="w-full text-white text-sm font-medium py-2 px-4 rounded transition-colors"
            style={{ backgroundColor: '#0e7572' }}
            onMouseOver={e => e.currentTarget.style.backgroundColor = '#0c6360'}
            onMouseOut={e => e.currentTarget.style.backgroundColor = '#0e7572'}
            onClick={() => setSubmitted(true)}
          >
            Submit
          </button>
          {submitted && (
            <p role="status" className="mt-2 text-center text-xs text-emerald-700">
              Preview complete — no response was submitted.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormPreviewRenderer;
